import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { selectRequiredValidator } from '../../util/select-required-validator.validator';
import { PreguntaCatalogo } from '../../model/catalogo.model';
import { CampoProductoVC } from '../../model/pack.model';
import { CommonModule } from '@angular/common';
import { CaInputDirective } from '@global-front-components/ui/input';
import { CaError, CaFormFieldComponent, CaHint, CaLabel } from '@global-front-components/ui/form-field';
import { CaDatepickerComponent } from '@global-front-components/ui/datepicker';
import { CaSelectComponent } from '@global-front-components/ui/select';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-pregunta-venta-cruzada',
  imports: [
    CommonModule,
    CaFormFieldComponent,
    CaLabel,
    CaInputDirective,
    CaHint,
    ReactiveFormsModule,
    CaError,
    CaDatepickerComponent,
    CaSelectComponent,
    CommonModule,
    TranslateModule
  ],
  templateUrl: './pregunta-venta-cruzada.component.html',
  styleUrls: ['./pregunta-venta-cruzada.component.scss'],
  standalone: true
})
export class PreguntaVentaCruzadaComponent implements OnInit {
  @Input() campoProductoVC!: CampoProductoVC;
  @Input() form!: FormGroup;
  codCampo: string;
  pregunta: PreguntaCatalogo;

  ngOnInit(): void {
    this.codCampo = this.campoProductoVC.codCampo;
    this.pregunta = this.campoProductoVC.preguntaCatalogo;
    this.anadirControlPreguntaForm();
  }

  private anadirControlPreguntaForm() {
    if (!this.pregunta || !this.campoProductoVC.visible) return;
    if (this.pregunta.tipoRespuesta === '1') {
      this.form.addControl(this.codCampo, new FormControl(this.pregunta.descRespuesta || -1, [Validators.required, selectRequiredValidator()]));
    } else if (this.pregunta.tipoRespuesta === '2') {
      this.form.addControl(this.codCampo, new FormControl(this.pregunta.idRespuesta || -1, [Validators.required, selectRequiredValidator()]));
    } else if (this.pregunta.tipoRespuesta === '3' || this.pregunta.tipoRespuesta === '4') {
      this.form.addControl(this.codCampo, new FormControl(this.getValorCampo(), [Validators.required, Validators.min(1)]));
    } else if (this.pregunta.tipoRespuesta === '5') {
      let fechaMili = Date.now();

      // candidatos en orden de preferencia: valor explícito, descRespuesta, valorDefecto, valorRespDefecto
      const candidatos = [
        this.campoProductoVC?.valor,
        this.pregunta?.descRespuesta,
        this.campoProductoVC?.valorDefecto,
        this.pregunta?.valorRespDefecto
      ];

      for (const v of candidatos) {
        const t = this.parseFechaInput(v);
        if (t != null) {
          fechaMili = t;
          break;
        }
      }

      this.form.addControl(this.codCampo, new FormControl(fechaMili, [Validators.required, Validators.min(1)]));
    } else if (this.pregunta.tipoRespuesta === '6') {
      this.form.addControl(this.codCampo, new FormControl(this.getValorCampo(), [Validators.required, Validators.minLength(1)]));
    }
  }

  public seleccionaRespuesta(pregunta: PreguntaCatalogo): void {
    const selectedId = this.form.get(this.codCampo)?.value;
    if (selectedId == -1) {
      pregunta.descRespuesta = '';
      pregunta.idRespuesta = -1;
    }
    pregunta.respuestas.forEach(r => {
      if (r.idRespuesta === Number(selectedId)) {
        r.seleccionada = true;
        pregunta.descRespuesta = String(r.idRespuesta);
        pregunta.idRespuesta = r.idRespuesta;
      } else {
        r.seleccionada = false;
      }
    })
  }

  public cambiarRespuesta(pregunta: PreguntaCatalogo, e: Event): void {
    pregunta.descRespuesta = (e.target as HTMLInputElement).value;
  }

  private getValorCampo(): string {
    return this.campoProductoVC.valor || this.campoProductoVC.valorDefecto || this.pregunta.valorRespDefecto || ''
  }

  /** Intenta parsear fecha desde distintos formatos:
 *  - Date
 *  - number (epoch ms)
 *  - string numérica (segundos/ms desde epoch)
 *  - string en formato dd/MM/yyyy
 *  - como último recurso: new Date(string)
 * Devuelve epoch (ms) o null si no reconoce el formato.
 */
  private parseFechaInput(val: any): number | null {
    if (val == null) return null;

    // Date
    if (val instanceof Date) {
      const t = val.getTime();
      return Number.isNaN(t) ? null : t;
    }

    // number (epoch ms)
    if (typeof val === 'number' && Number.isFinite(val)) {
      return val;
    }

    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return null;

      // string numérica → epoch (10 dígitos ≈ segundos, 13 ≈ milisegundos)
      if (/^-?\d+$/.test(s)) {
        let n = parseInt(s, 10);
        if (s.length <= 10) n *= 1000; // segundos → ms
        return n;
      }

      // dd/MM/yyyy
      const ddmmyyyy = this.tryParseDdMmYyyy(s);
      if (ddmmyyyy != null) return ddmmyyyy;

      // último recurso: Date nativo (ISO u otros reconocidos por el motor)
      const d = new Date(s);
      const t = d.getTime();
      if (!Number.isNaN(t)) return t;
    }

    return null;
  }

  /** Parse estricto dd/MM/yyyy → epoch (ms), o null si no encaja */
  private tryParseDdMmYyyy(s: string): number | null {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (!m) return null;
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1; // 0-11
    const y = parseInt(m[3], 10);

    const dt = new Date(y, mo, d);
    // Validación estricta (evita 32/13/2026)
    if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) {
      return dt.getTime();
    }
    return null;
  }

}